import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.filterMedia();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
    newMediaGallery.filterMedia(event.detail.resource);
  };

  /**
   * Finds the currently selected size option value from the variant picker.
   * @returns {string} The selected size string.
   */
  getSelectedSize() {
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) return '';

    // 1. Search in fieldsets (radio buttons)
    const fieldsets = variantPicker.querySelectorAll('fieldset');
    for (const fieldset of fieldsets) {
      const legend = fieldset.querySelector('legend');
      if (legend && legend.textContent.toLowerCase().includes('size')) {
        const checkedRadio = fieldset.querySelector('input:checked');
        if (checkedRadio) return checkedRadio.value.trim();
      }
    }

    // 2. Search in selects (dropdowns)
    const selects = variantPicker.querySelectorAll('select');
    for (const select of selects) {
      const label = variantPicker.querySelector(`label[for="${select.id}"]`);
      if (label && label.textContent.toLowerCase().includes('size')) {
        return select.value.trim();
      }
    }

    // 3. Fallback: search all checked inputs / selected options for size names
    const checked = variantPicker.querySelectorAll('input:checked, option[selected]');
    for (const el of checked) {
      const val = (el.value || el.textContent || '').trim();
      const valLower = val.toLowerCase();
      if (['single', 'double', 'queen', 'king'].some(size => valLower.includes(size))) {
        return val;
      }
    }

    return '';
  }

  /**
   * Dynamically filters slides, thumbnails, and zoom elements in the DOM based on the selected variant size.
   * Uses CSS class toggling rather than [hidden] attribute to avoid conflict with the slideshow's
   * [reveal] mechanism (which re-shows [hidden] slides when slideshow.select() is called).
   * @param {Object} [selectedVariant] - The selected variant object from Shopify (has .options, .featured_media).
   */
  filterMedia(selectedVariant = null) {
    // --- 1. Determine selected size ---
    let selectedSize = '';
    let featuredMediaIdFromVariant = null;

    if (selectedVariant) {
      // Use featured_media.id directly from the variant object — most reliable
      if (selectedVariant.featured_media?.id) {
        featuredMediaIdFromVariant = String(selectedVariant.featured_media.id);
      }

      // Find size option index from variant-picker DOM
      const variantPicker = document.querySelector('variant-picker');
      let sizeIndex = -1;
      if (variantPicker) {
        const fieldsets = variantPicker.querySelectorAll('fieldset');
        fieldsets.forEach((fieldset, idx) => {
          const legend = fieldset.querySelector('legend');
          if (legend && legend.textContent.toLowerCase().includes('size')) sizeIndex = idx;
        });
        if (sizeIndex === -1) {
          variantPicker.querySelectorAll('select').forEach((select, idx) => {
            const label = variantPicker.querySelector(`label[for="${select.id}"]`);
            if (label && label.textContent.toLowerCase().includes('size')) sizeIndex = idx;
          });
        }
      }

      if (sizeIndex !== -1 && selectedVariant.options?.[sizeIndex]) {
        selectedSize = selectedVariant.options[sizeIndex];
      } else {
        for (const opt of (selectedVariant.options || [])) {
          if (['single', 'double', 'queen', 'king', 'super', 'split', 'long'].some(s => opt.toLowerCase().includes(s))) {
            selectedSize = opt;
            break;
          }
        }
        if (!selectedSize && selectedVariant.title) selectedSize = selectedVariant.title;
      }
    }

    if (!selectedSize) selectedSize = this.getSelectedSize();
    if (!selectedSize) return;

    // --- 2. Parse the variant→media mapping embedded by Liquid ---
    const mappingScript = this.querySelector('script[data-media-size-mapping]');
    if (!mappingScript) return;

    let mapping = [];
    try {
      mapping = JSON.parse(mappingScript.textContent);
    } catch (e) {
      console.error('[HubGallery] Failed to parse media size mapping:', e);
      return;
    }

    // --- 3. Compute which media IDs to hide and which featured media to show ---
    const currentSizeItem = mapping.find(item => item.size.toLowerCase() === selectedSize.toLowerCase());
    const featuredMediaId = featuredMediaIdFromVariant
      || (currentSizeItem ? String(currentSizeItem.media_id) : null);

    const hideMediaIds = new Set(
      mapping
        .filter(item => item.size.toLowerCase() !== selectedSize.toLowerCase())
        .map(item => String(item.media_id))
    );

    // --- 4. Hide/show slideshow-slide elements via CLASS only (not [hidden] attribute) ---
    // We MUST NOT touch [hidden] on slideshow-slide because the slideshow JS adds [reveal]
    // to force-show any [hidden] slide it tries to select, defeating display:none.
    // Instead we use the is-unrelated-variant-image CSS class which has display:none !important
    // in the stylesheet and is not affected by the slideshow's reveal mechanism.
    const allSlides = this.querySelectorAll('slideshow-slide[data-media-id], slideshow-slide[slide-id]');
    allSlides.forEach(slide => {
      const mediaId = String(slide.dataset.mediaId || slide.getAttribute('slide-id') || '');
      if (hideMediaIds.has(mediaId)) {
        slide.classList.add('is-unrelated-variant-image');
        slide.style.setProperty('display', 'none', 'important');
      } else {
        slide.classList.remove('is-unrelated-variant-image');
        slide.style.removeProperty('display');
      }
    });

    // --- 5. Hide/show thumbnail, grid, zoom, and other [data-media-id] elements ---
    const otherElements = this.querySelectorAll('[data-media-id]:not(slideshow-slide)');
    otherElements.forEach(el => {
      const mediaId = String(el.dataset.mediaId);
      if (hideMediaIds.has(mediaId)) {
        el.classList.add('is-unrelated-variant-image');
        el.setAttribute('hidden', '');
        el.style.setProperty('display', 'none', 'important');
      } else {
        el.classList.remove('is-unrelated-variant-image');
        el.removeAttribute('hidden');
        el.style.removeProperty('display');
      }
    });

    // --- 6. Navigate slideshow to the featured media slide ---
    if (featuredMediaId && this.slideshow) {
      // Use slide-id based selection which is more reliable than index
      // slideshow.select({id}) handles its own aria-hidden/scroll internally
      if (typeof this.slideshow.select === 'function') {
        // Ensure the target slide is not blocked by our class before selecting
        const targetSlide = this.querySelector(`slideshow-slide[slide-id="${featuredMediaId}"]`);
        if (targetSlide) {
          targetSlide.classList.remove('is-unrelated-variant-image');
          targetSlide.style.removeProperty('display');
        }
        this.slideshow.select({ id: String(featuredMediaId) }, undefined, { animate: false });
      }
    }

    // --- 7. Re-index visible thumbnail click handlers ---
    const visibleThumbnails = Array.from(this.querySelectorAll('.slideshow-controls__thumbnail:not([hidden])'));
    visibleThumbnails.forEach((thumb, idx) => {
      thumb.setAttribute('on:click', `/select/${idx}`);
    });

    const visibleZoomThumbnails = Array.from(this.querySelectorAll('.dialog-thumbnails-list__thumbnail:not([hidden])'));
    visibleZoomThumbnails.forEach((thumb, idx) => {
      thumb.setAttribute('on:click', `/handleThumbnailClick/${idx}`);
      thumb.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${idx}`);
    });

    const visibleZoomButtons = Array.from(this.querySelectorAll('.product-media-container__zoom-button:not([hidden])'));
    visibleZoomButtons.forEach((btn, idx) => {
      btn.setAttribute('on:click', `/zoom/${idx}`);
      btn.setAttribute('on:pointerenter', `/preloadImage/${idx}`);
    });
  }




  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
